import conductionLogo from '@/assets/images/conduction-logo.png';
import { SessionContext } from '@/context/SessionContext';
import {
	Box,
	Burger,
	Button,
	Flex,
	Group,
	Image,
	Menu,
	Text,
	useMantineTheme
} from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import {
	IconAlertCircle,
	IconAt,
	IconChevronDown,
	IconExternalLink,
	IconLogout,
} from '@tabler/icons-react';
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import appSettings from '../../appsettings.json';
import LoginModal from '../modals/LoginModal';
import PasswordResetModal from '../modals/PasswordResetModal';
import UserSettingsModal from '../modals/UserSettingsModal';

const Header: React.FC = () => {
	const session = useContext(SessionContext);
	if (!session) {
		throw new Error('Header must be a child of SessionContext.');
	}
	const navigate = useNavigate();

	const user = session?.user;
	const theme = useMantineTheme();
	const isMobile = useMediaQuery(`(max-width: ${theme.breakpoints.sm})`);

	const [showLoginModal, setShowLoginModal] = useState(false);
	const [showPasswordResetModal, setShowPasswordResetModal] = useState<boolean>(
		window.location.pathname.startsWith('/password-reset')
	);
	const [showUserSettingsModal, setShowUserSettingsModal] = useState<boolean>(false);

	const [searchParams] = useSearchParams();

	const emailConfirmation = useCallback(
		async (userId: string, emailToken: string) => {
			if (userId && emailToken) {
				await session.confirmEmail(userId, emailToken);
			}
		},
		[session]
	);

	const hasConfirmedEmail = useRef(false);
	useEffect(() => {
		const userId = searchParams.get('userId');
		const emailToken = searchParams.get('email-token');
		if (userId && emailToken && !hasConfirmedEmail.current) {
			hasConfirmedEmail.current = true;
			// clear the search parameters after reading them
			navigate('/', { replace: true });
			emailConfirmation(userId, emailToken);
		}
	}, [emailConfirmation, searchParams, navigate]);

	/* ------------------------------ nav measurement ------------------------------ */

	// Visible container for nav links (center area)
	const navContainerRef = useRef<HTMLDivElement | null>(null);
	// Hidden off-screen measurement container that always renders the nav buttons
	const measureRef = useRef<HTMLDivElement | null>(null);
	const [collapseToBurger, setCollapseToBurger] = useState(false);

	const recomputeOverflow = useCallback(() => {
		const visible = navContainerRef.current;
		const measure = measureRef.current;
		if (!visible || !measure) return;
		const needed = measure.scrollWidth; // total width of all buttons
		const available = visible.clientWidth; // space allocated to nav
		setCollapseToBurger(needed > available);
	}, []);

	useEffect(() => {
		const visible = navContainerRef.current;
		const measure = measureRef.current;
		if (!visible || !measure) return;

		const ro = new ResizeObserver(() => {
			recomputeOverflow();
		});
		ro.observe(visible);
		ro.observe(measure);

		// Extra safety on window resize & route changes
		window.addEventListener('resize', recomputeOverflow);
		recomputeOverflow();

		return () => {
			ro.disconnect();
			window.removeEventListener('resize', recomputeOverflow);
		};
	}, [recomputeOverflow, user, isMobile]);

	const path = typeof window !== 'undefined' ? window.location.pathname : '';

	/* ------------------------------ nav definitions ------------------------------ */

	// Build a flat list of nav actions for reuse (buttons or burger items)
	const navActions = useMemo(() => {
		if (!user) return [];

		const items: Array<{
			key: string;
			label: string;
			active?: boolean;
			onClick: () => void;
		}> = [];

		// Videos
		items.push({
			key: 'videos',
			label: 'Videos',
			active: path === `/videos`,
			onClick: () => navigate(`/videos`),
		});

		// Activity Feed
		items.push({
			key: 'activity-feed',
			label: 'Activity Feed',
			active: path.includes(`/activity-feed`) || path === "/",
			onClick: () => navigate(`/activity-feed`),
		});

		// Classroom Controls (ClassroomAdmin)
		if (user.roles?.includes('ClassroomAdmin')) {
			const url = `/classroom/${user.classroomId}`;
			items.push({
				key: 'classroom-controls',
				label: 'Classroom Controls',
				active: path === url,
				onClick: () => navigate(url),
			});
		}

		// Instrumentalist Interface
		if (user.roles?.includes('Instrumentalist')) {
			items.push({
				key: 'instrumentalist',
				label: 'Instrumentalist Interface',
				active: path === `/instrumentalist`,
				onClick: () => navigate(`/instrumentalist`),
			});
		}

		// Video Editor Interface
		if (user.isVideoEditor) {
			items.push({
				key: 'video-editor',
				label: 'Video Editor Interface',
				active: path === `/video-editor`,
				onClick: () => navigate(`/video-editor`),
			});
		}

		// Instrumentalists Admin + Project Submissions (ConductionAdmin or InstrumentalistLiaison)
		if (user.roles?.includes('ConductionAdmin') || user.roles?.includes('InstrumentalistLiaison')) {
			items.push({
				key: 'instrumentalists-admin',
				label: 'Instrumentalists',
				active: path === `/instrumentalists-admin`,
				onClick: () => navigate(`/instrumentalists-admin`),
			});
			items.push({
				key: 'video-editors-admin',
				label: 'Video Editors',
				active: path === `/video-editors-admin`,
				onClick: () => navigate(`/video-editors-admin`),
			});
			items.push({
				key: 'project-submissions-admin',
				label: 'Project Submissions',
				active: path === `/project-submissions-admin`,
				onClick: () => navigate(`/project-submissions-admin`),
			});
		}

		// Classrooms (ConductionAdmin)
		if (user.roles?.includes('ConductionAdmin')) {
			items.push({
				key: 'classrooms-admin',
				label: 'Classrooms',
				active: path === `/classrooms-admin`,
				onClick: () => navigate(`/classrooms-admin`),
			});
		}

		// Classrooms (ConductionAdmin)
		if (user.roles?.includes('ConductionAdmin')) {
			items.push({
				key: 'users-admin',
				label: 'Users',
				active: path === `/users-admin`,
				onClick: () => navigate(`/users-admin`),
			});
		}

		// Artists
		if (Array.isArray(user.artists) && user.artists.length > 0) {
			if (user.artists.length === 1) {
				items.push({
					key: 'my-artist',
					label: 'My Artist',
					active: path === `/artist/${user.artists[0].artistId}`,
					onClick: () => navigate(`/artist/${user.artists[0].artistId}`),
				});
			} else {
				// For burger: add each artist as its own item.
				// For inline buttons: we’ll render a dropdown instead (below).
				for (const a of user.artists) {
					items.push({
						key: `artist-${a.artistId}`,
						label: `My Artist: ${a.artistName}`,
						active: path === `/artist/${a.artistId}`,
						onClick: () => navigate(`/artist/${a.artistId}`),
					});
				}
			}
		}

		return items;
	}, [navigate, path, user]);

	/* --------------------------------- renderers -------------------------------- */

	const renderInlineNav = () => {
		if (!user) return null;

		// Inline buttons container (this is what users see when there's room)
		return (
			<Flex gap={isMobile ? 4 : 'sm'} wrap="nowrap" align="center">
				{/* Videos */}
				<Button
					w="auto"
					h={isMobile ? 20 : 30}
					onClick={() => navigate(`/videos`)}
					bg={path === `/videos` ? theme.colors.white?.[6] : undefined}
					c={path === `/videos` ? theme.colors.blue?.[8] : undefined}
				>
					<Text fz={isMobile ? 8 : 14}>Videos</Text>
				</Button>

				{/* Activity Feed */}
				<Button
					w="auto"
					h={isMobile ? 20 : 30}
					onClick={() => navigate(`/activity-feed`)}
					bg={path.includes(`/activity-feed`) || path === "/" ? theme.colors.white?.[6] : undefined}
					c={path.includes(`/activity-feed`) || path === "/" ? theme.colors.blue?.[8] : undefined}
				>
					<Text fz={isMobile ? 8 : 14}>Activity Feed</Text>
				</Button>

				{/* Classroom Controls */}
				{user.roles?.includes('ClassroomAdmin') && (
					<Button
						w="auto"
						h={isMobile ? 20 : 30}
						onClick={() => navigate(`/classroom/${user.classroomId}`)}
						bg={path === `/classroom/${user.classroomId}` ? theme.colors.white?.[6] : undefined}
						c={path === `/classroom/${user.classroomId}` ? theme.colors.blue?.[8] : undefined}
					>
						<Text fz={isMobile ? 8 : 14}>Classroom Controls</Text>
					</Button>
				)}

				{/* Instrumentalist Interface */}
				{user.roles?.includes('Instrumentalist') && (
					<Button
						w="auto"
						h={isMobile ? 20 : 30}
						onClick={() => navigate(`/instrumentalist`)}
						bg={path === `/instrumentalist` ? theme.colors.white?.[6] : undefined}
						c={path === `/instrumentalist` ? theme.colors.blue?.[8] : undefined}
					>
						<Text fz={isMobile ? 8 : 14}>Instrumentalist Interface</Text>
					</Button>
				)}

				{/* Video Editor Interface */}
				{user.isVideoEditor && (
					<Button
						w="auto"
						h={isMobile ? 20 : 30}
						onClick={() => navigate(`/video-editor`)}
						bg={path === `/video-editor` ? theme.colors.white?.[6] : undefined}
						c={path === `/video-editor` ? theme.colors.blue?.[8] : undefined}
					>
						<Text fz={isMobile ? 8 : 14}>Video Editor Interface</Text>
					</Button>
				)}

				{/* Instrumentalists / Project Submissions */}
				{(user.roles?.includes('ConductionAdmin') || user.roles?.includes('InstrumentalistLiaison')) && (
					<>
						<Button
							w="auto"
							h={isMobile ? 20 : 30}
							onClick={() => navigate(`/instrumentalists-admin`)}
							bg={path === `/instrumentalists-admin` ? theme.colors.white?.[6] : undefined}
							c={path === `/instrumentalists-admin` ? theme.colors.blue?.[8] : undefined}
						>
							<Text fz={isMobile ? 8 : 14}>Instrumentalists</Text>
						</Button>
						<Button
							w="auto"
							h={isMobile ? 20 : 30}
							onClick={() => navigate(`/video-editors-admin`)}
							bg={path === `/video-editors-admin` ? theme.colors.white?.[6] : undefined}
							c={path === `/video-editors-admin` ? theme.colors.blue?.[8] : undefined}
						>
							<Text fz={isMobile ? 8 : 14}>Video Editors</Text>
						</Button>
						<Button
							w="auto"
							h={isMobile ? 20 : 30}
							onClick={() => navigate(`/project-submissions-admin`)}
							bg={path === `/project-submissions-admin` ? theme.colors.white?.[6] : undefined}
							c={path === `/project-submissions-admin` ? theme.colors.blue?.[8] : undefined}
						>
							<Text fz={isMobile ? 8 : 14}>Project Submissions</Text>
						</Button>
					</>
				)}

				{/* Classrooms */}
				{user.roles?.includes('ConductionAdmin') && (
					<Button
						w="auto"
						h={isMobile ? 20 : 30}
						onClick={() => navigate(`/classrooms-admin`)}
						bg={path === `/classrooms-admin` ? theme.colors.white?.[6] : undefined}
						c={path === `/classrooms-admin` ? theme.colors.blue?.[8] : undefined}
					>
						<Text fz={isMobile ? 8 : 14}>Classrooms</Text>
					</Button>
				)}

				{/* Users */}
				{user.roles?.includes('ConductionAdmin') && (
					<Button
						w="auto"
						h={isMobile ? 20 : 30}
						onClick={() => navigate(`/users-admin`)}
						bg={path === `/users-admin` ? theme.colors.white?.[6] : undefined}
						c={path === `/users-admin` ? theme.colors.blue?.[8] : undefined}
					>
						<Text fz={isMobile ? 8 : 14}>Users</Text>
					</Button>
				)}

				{/* Artists */}
				{Array.isArray(user.artists) && user.artists.length > 0 && (
					user.artists.length === 1 ? (
						<Button
							w={isMobile ? 40 : 100}
							h={isMobile ? 20 : 30}
							p={0}
							onClick={() => navigate(`/artist/${user.artists[0].artistId}`)}
						>
							<Text fz={isMobile ? 8 : 14}>My Artist</Text>
						</Button>
					) : (
						<Menu transitionProps={{ transition: 'pop-top-right' }} trigger="click-hover" openDelay={100}>
							<Menu.Target>
								<Button
									variant="subtle"
									w={isMobile ? 70 : 120}
									h={isMobile ? 20 : 30}
									p={0}
									bg={theme.colors.blue[8]}
									c={theme.colors.blue[0]}
								>
									<Group gap="xs">
										<Text fz={isMobile ? 8 : 14}>My Artists</Text>
										<IconChevronDown size={16} />
									</Group>
								</Button>
							</Menu.Target>
							<Menu.Dropdown>
								{user.artists.map((artist: any) => (
									<Menu.Item
										key={artist.artistId}
										onClick={() => navigate(`/artist/${artist.artistId}`)}
									>
										<Text size={isMobile ? '10px' : '14px'}>{artist.artistName}</Text>
									</Menu.Item>
								))}
							</Menu.Dropdown>
						</Menu>
					)
				)}
			</Flex>
		);
	};

	const renderBurgerNav = () => {
		if (!user) return null;

		// Burger menu with all actions flattened (including each artist as its own item)
		return (
			<Menu transitionProps={{ transition: 'pop-top-left' }} position="bottom-start" trigger="click-hover" openDelay={100}>
				<Menu.Target>
					<Burger aria-label="Open navigation menu" size="sm" />
				</Menu.Target>
				<Menu.Dropdown>
					{navActions.map((item) => (
						<Menu.Item
							key={item.key}
							onClick={item.onClick}
							// a subtle hint for the active route
							fw={item.active ? 700 : 400}
						>
							{item.label}
						</Menu.Item>
					))}
				</Menu.Dropdown>
			</Menu>
		);
	};

	/* --------------------------------- component -------------------------------- */

	return (
		<Box px="md" mt="xs" style={{ width: '100%' }}>
			<LoginModal showModal={showLoginModal} setShowModal={setShowLoginModal} />
			<PasswordResetModal showModal={showPasswordResetModal} setShowModal={setShowPasswordResetModal} />
			<UserSettingsModal showModal={showUserSettingsModal} setShowModal={setShowUserSettingsModal} />

			{/* Three-column header: logo | nav (auto) | user menu */}
			<Flex align="center" gap="sm" w="100%" h={20}>
				{/* Center: Nav (auto width, collapses when overflowing) */}
				<Box
					ref={navContainerRef}
					style={{
						flex: 1,
						minWidth: 0, // allow shrinking so overflow can be detected
						overflow: 'hidden', // hide when overflowing to trigger measurement logic
						display: 'flex',
						alignItems: 'center',
					}}
				>
					{user && (collapseToBurger ? renderBurgerNav() : renderInlineNav())}

					{/* Hidden measurement container – identical to inline buttons for accurate width calc */}
					<Box
						ref={measureRef}
						aria-hidden
						style={{
							position: 'absolute',
							visibility: 'hidden',
							pointerEvents: 'none',
							height: 0,
							overflow: 'visible',
							whiteSpace: 'nowrap',
						}}
					>
						{renderInlineNav()}
					</Box>
				</Box>

				{/* Right: User menu / Login */}
				<Group justify="flex-end" wrap="nowrap">
					{user == null ? (
						<Button variant="outline" size="xs" onClick={() => setShowLoginModal(true)}>
							<Text size="sm">Login</Text>
						</Button>
					) : (
						<Menu transitionProps={{ transition: 'pop-top-right' }} trigger="click-hover" openDelay={100} position="bottom-end">
							<Menu.Target>
								<Button variant="subtle" p={0} justify="right">
									<Group gap="xs">
										{session.profileAvatar}
										<Text
											fz={isMobile ? 12 : 14}
											style={{
												overflow: 'hidden',
												textOverflow: 'ellipsis',
												whiteSpace: 'nowrap',
												maxWidth: isMobile ? 320 : 350,
											}}
										>
											{user.username}
										</Text>
										{(!user.emailConfirmed || user.agreedToCurrentTos === false) && (
											<IconAlertCircle color={theme.colors.yellow[6]} size={20} />
										)}
										<IconChevronDown size={16} />
									</Group>
								</Button>
							</Menu.Target>
							<Menu.Dropdown>
								{!user.roles?.includes('Student') && (
									<Menu.Item
										leftSection={<IconAt size={16} />}
										onClick={() => setShowUserSettingsModal(true)}
										c={user.emailConfirmed ? theme.primaryColor : theme.colors.yellow[6]}
									>
										<Group>
											<Text size={isMobile ? '12px' : '14px'}>User Settings</Text>
											{(!user.emailConfirmed || user.agreedToCurrentTos === false) && (
												<IconAlertCircle
													size={16}
													color={theme.colors.yellow[6]}
													style={{ marginLeft: '8px' }}
												/>
											)}
										</Group>
									</Menu.Item>
								)}

								<Menu.Item
									leftSection={<IconLogout size={16} />}
									onClick={() => {
										session?.logout();
									}}
								>
									<Text size={isMobile ? '12px' : '14px'}>Logout</Text>
								</Menu.Item>
							</Menu.Dropdown>
						</Menu>
					)}

					{!isMobile && (
						<Button
							variant="subtle"
							size="xs"
							leftSection={<Image src={conductionLogo} alt="Conduction App" height={24} radius="sm" />}
							onClick={() => window.open(`${appSettings.AppBaseUrl}`, "_blank")}
						>
							<Group gap={4}>
								<Text size="sm">Make a song now!</Text>
								<IconExternalLink size={16} />
							</Group>
						</Button>
					)}
				</Group>
			</Flex>
		</Box>
	);
};

export default Header;
